import React, { useState, useEffect } from 'react';
import { User, Lesson, Badge, Stats as LessonStats, KeystrokeEvent } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import TypingTutor from '../core/TypingTutor';
import TypingGame from '../core/TypingGame';
import MultiplayerRace from '../core/MultiplayerRace';
import StudentProgressTab from './StudentProgressTab';
import { api } from '../../utils/api';

// NEW: Import refactored components
import WordBurstGame from '../games/WordBurstGame';
import BubblePopGame from '../games/BubblePopGame';
import CarRaceGame from '../games/CarRaceGame';
import TypingRpgGame from '../games/TypingRpgGame';
import FallingWordsGame from '../games/FallingWordsGame';
import SearchOfThe67Game from '../games/SearchOfThe67Game';
import TurkeyChaseGame from '../games/TurkeyChaseGame';
import CheckpointGame from '../games/CheckpointGame';
import { CATALOG_GAMES } from '../games/gameCatalog';
import GameCard from '../ui/GameCard';
import GameContainer from '../ui/GameContainer';
import LessonCard from '../ui/LessonCard';
import LessonSectionHeader from '../ui/LessonSectionHeader';
import BadgesTab from './BadgesTab';


// --- MAIN STUDENT DASHBOARD ---

interface StudentDashboardProps {
  user: User;
  onNewBadges: (badges: Badge[]) => void;
}

const StudentDashboard: React.FC<StudentDashboardProps> = ({ user, onNewBadges }) => {
  const [tab, setTab] = useState('lessons');
  const [homeworkLessons, setHomeworkLessons] = useState<Lesson[]>([]);
  const [systemTutorialLessons, setSystemTutorialLessons] = useState<Lesson[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<{ [key: string]: { wpm: number; accuracy: number } }>({});
  const [tutorialProgress, setTutorialProgress] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedGame, setSelectedGame] = useState<string | null>(null);
  const [classId, setClassId] = useState<string | null>(null);
  const [checkpoint, setCheckpoint] = useState<{ words: string[]; rangeLabel: string; nextLesson: Lesson | null } | null>(null);

  const CHECKPOINT_INTERVAL = 5;

  const buildWordPool = (lessons: Lesson[]): string[] => {
    const text = lessons.map(l => l.lesson_text || '').join(' ');
    return text.split(/[^a-zA-Z0-9']+/).map(w => w.trim()).filter(w => w.length > 0);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [enrollData, tutorialProgressData, systemLessonsData, lessonResults] = await Promise.all([
                api.get<{ class_id?: string }>(`/api/v1/student/${user.user_id}/class`),
                api.get<string[]>(`/api/v1/student/${user.user_id}/tutorial-progress`),
                api.get<Lesson[]>('/api/v1/lessons/system'),
                api.get<{ lesson_id: number; wpm: number; accuracy: number }[]>(`/api/v1/student/${user.user_id}/progress`).catch(() => [])
            ]);

            setSystemTutorialLessons(systemLessonsData);

            // System lessons have numeric ids and are saved as regular lesson results,
            // so they count toward the lesson map too.
            const progressMap: { [key: string]: boolean } = {};
            (tutorialProgressData || []).forEach(lessonId => { progressMap[lessonId] = true; });
            lessonResults.forEach(r => { progressMap[String(r.lesson_id)] = true; });
            setTutorialProgress(progressMap);
            setProgress(Object.fromEntries(lessonResults.map(r => [String(r.lesson_id), { wpm: r.wpm, accuracy: r.accuracy }])));

            if (enrollData.class_id) {
                setClassId(enrollData.class_id);
                const assigned = await api.get<Lesson[]>(`/api/v1/class/${enrollData.class_id}/assignments`);
                setHomeworkLessons(assigned);
            }
        } catch (e) {
            if (e instanceof Error) setError(e.message);
        } finally {
            setLoading(false);
        }
    };
    if (tab === 'lessons') fetchDashboardData();
  }, [user.user_id, tab]);

  const handleLessonComplete = async (lessonId: string | number, stats: Omit<LessonStats, 'errors'>, errors?: Array<{ expected: string, typed: string }>, keystrokeLog?: KeystrokeEvent[]) => {
    const originalLessonId = typeof lessonId === 'string' ? lessonId.split(' ')[0] : lessonId;
    const isTutorial = typeof originalLessonId === 'string' && originalLessonId.startsWith('tutorial-');

    if (isTutorial) {
        setTutorialProgress(prev => ({ ...prev, [originalLessonId]: true }));
        try {
            await api.post('/api/v1/student/tutorial-progress', {
                student_user_id: user.user_id,
                lesson_id_string: originalLessonId
            });
        } catch (e) {
            console.error("Error saving tutorial progress:", e);
        }
    }
    else {
      try {
        const result = await api.post<any>('/api/v1/progress', { 
            student_user_id: user.user_id, 
            lesson_id: originalLessonId, 
            wpm: stats.wpm, 
            accuracy: stats.acc, 
            errors: errors || [],
            keystrokeLog: keystrokeLog || []
        });
        if (result.newBadges?.length > 0) onNewBadges(result.newBadges);
        setProgress(prev => ({ ...prev, [String(originalLessonId)]: { wpm: stats.wpm, accuracy: stats.acc } }));
        setTutorialProgress(prev => ({ ...prev, [String(originalLessonId)]: true }));
      } catch (e) { console.error("Error saving progress:", e); }
    }
  };

  const handleExitTutor = () => {
    setSelectedLesson(null);
  };

  const renderContent = () => {
    if (tab === 'lessons') {
      if (loading) return <LoadingSpinner message="Loading lessons..." />;
      if (error) return <div className="text-red-500 text-center bg-red-100 p-3 rounded-md">{error}</div>;
      if (checkpoint) {
        return (
          <CheckpointGame
            words={checkpoint.words}
            rangeLabel={checkpoint.rangeLabel}
            onContinue={() => {
              const next = checkpoint.nextLesson;
              setCheckpoint(null);
              setSelectedLesson(next);
            }}
          />
        );
      }
      if (selectedLesson) {
        const allLessons = [...homeworkLessons, ...systemTutorialLessons];
        const currentIndex = allLessons.findIndex(l => l.lesson_id === selectedLesson.lesson_id);
        const nextLesson = (currentIndex !== -1 && currentIndex + 1 < allLessons.length) ? allLessons[currentIndex + 1] : null;

        const tutorialIdx = systemTutorialLessons.findIndex(l => String(l.lesson_id) === String(selectedLesson.lesson_id));
        const isCheckpointLesson = tutorialIdx !== -1 && (tutorialIdx + 1) % CHECKPOINT_INTERVAL === 0;

        const handleStartNextLesson = (isCheckpointLesson || nextLesson) ? () => {
          if (isCheckpointLesson) {
            const rangeStart = tutorialIdx - CHECKPOINT_INTERVAL + 2; // 1-indexed start of this block
            const poolLessons = systemTutorialLessons.slice(Math.max(0, tutorialIdx - CHECKPOINT_INTERVAL + 1), tutorialIdx + 1);
            setCheckpoint({
              words: buildWordPool(poolLessons),
              rangeLabel: `Lessons ${rangeStart}-${tutorialIdx + 1}`,
              nextLesson,
            });
            setSelectedLesson(null);
          } else if (nextLesson) {
            setSelectedLesson(nextLesson);
          }
        } : undefined;

        return <TypingTutor
            key={selectedLesson.lesson_id.toString()}
            lesson={selectedLesson} 
            onLessonComplete={handleLessonComplete} 
            onExit={handleExitTutor}
            onStartNextLesson={handleStartNextLesson} 
        />;
      }

      if (systemTutorialLessons.length === 0) return <p>No tutorial lessons available at the moment.</p>;

      const tutorialIds = systemTutorialLessons.map(l => l.lesson_id);
      const lastCompletedIndex = tutorialIds.reduce((lastIndex, id, currentIndex) => {
          return tutorialProgress[String(id)] ? currentIndex : lastIndex;
      }, -1);
      const nextUnlockedIndex = Number(lastCompletedIndex) + 1;
      
      const homeRowLessons = systemTutorialLessons.slice(0, 23);
      const topRowLessons = systemTutorialLessons.slice(23, 51);
      const bottomRowLessons = systemTutorialLessons.slice(51, 88);
      const basicLevel1Lessons = systemTutorialLessons.slice(88, 126);
      const trickyWords1Lessons = systemTutorialLessons.slice(126, 137);
      const shiftKeyLessons = systemTutorialLessons.slice(137, 191);
      const commonPatternsLessons = systemTutorialLessons.slice(191, 202);
      const basicLevel2Lessons = systemTutorialLessons.slice(202, 233);
      const trickyWords2Lessons = systemTutorialLessons.slice(233, 244);
      const numbersLessons = systemTutorialLessons.slice(244, 274);
      const commonPatterns2Lessons = systemTutorialLessons.slice(274, 285);
      const basicLevel3Lessons = systemTutorialLessons.slice(285, 316);
      const symbolsLessons = systemTutorialLessons.slice(316, 346);
      const commonPatterns3Lessons = systemTutorialLessons.slice(346, 357);
      const advancedLevel1Lessons = systemTutorialLessons.slice(357, 388);
      const moreSymbolsLessons = systemTutorialLessons.slice(388, 418);
      const trickyWords3Lessons = systemTutorialLessons.slice(418, 429);
      const advancedLevel2Lessons = systemTutorialLessons.slice(429, 460);
      const advancedLevel3Lessons = systemTutorialLessons.slice(460, 491);
      const advancedLevel4Lessons = systemTutorialLessons.slice(491, 524);
      const advancedLevel5Lessons = systemTutorialLessons.slice(524, 557);
      const advancedLevel6Lessons = systemTutorialLessons.slice(557, 590);
      const advancedLevel7Lessons = systemTutorialLessons.slice(590, 621);
      const advancedLevel8Lessons = systemTutorialLessons.slice(621, 652);
      const advancedLevel9Lessons = systemTutorialLessons.slice(652, 670);


      let sectionCount = 0;
      const renderTutorialSection = (title: string, lessons: Lesson[], lessonStartIndex: number, subtitle?: string) => {
         const theme = sectionCount++;
         return (
         <div>
              <LessonSectionHeader title={title} subtitle={subtitle} theme={theme} total={lessons.length}
                done={lessons.filter(l => tutorialProgress[String(l.lesson_id)] === true).length} />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {lessons.map((lesson, index) => {
                    const overallIndex = lessonStartIndex + index;
                    const isComplete = tutorialProgress[String(lesson.lesson_id)] === true;
                    const isUnlocked = overallIndex <= nextUnlockedIndex;
                    
                    let status: 'completed' | 'unlocked' | 'locked' = 'locked';
                    if (isComplete) status = 'completed';
                    else if (isUnlocked) status = 'unlocked';
                    
                    return (
                        <LessonCard 
                          key={lesson.lesson_id}
                          lessonNumber={overallIndex + 1}
                          title={lesson.title}
                          status={status}
                          onClick={() => setSelectedLesson(lesson)}
                          isFirst={overallIndex === 0 && nextUnlockedIndex === 0}
                          isNext={overallIndex === nextUnlockedIndex}
                          theme={theme}
                        />
                    );
                })}
              </div>
            </div>
         );
      };


      return (
        <div className="space-y-12">
           {homeworkLessons.length > 0 && (
                <div>
                  <LessonSectionHeader title="Homework" theme={5} total={homeworkLessons.length}
                    done={homeworkLessons.filter(l => !!progress[String(l.lesson_id)]).length} />
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {homeworkLessons.map((lesson) => {
                      const isComplete = !!progress[String(lesson.lesson_id)];
                      const status = isComplete ? 'completed' : 'unlocked';
                      return (
                        <LessonCard 
                          key={lesson.lesson_id}
                          title={lesson.title}
                          status={status}
                          onClick={() => setSelectedLesson(lesson)}
                          theme={5}
                        />
                      );
                    })}
                  </div>
                </div>
            )}
            
           {renderTutorialSection("Home Row & Introduction", homeRowLessons, 0)}
           {renderTutorialSection("Top Row", topRowLessons, 23)}
           {renderTutorialSection("Bottom Row", bottomRowLessons, 51)}
           {renderTutorialSection("Basic Level 1", basicLevel1Lessons, 88)}
           {renderTutorialSection("Tricky Words 1", trickyWords1Lessons, 126)}
           {renderTutorialSection("Shift Key", shiftKeyLessons, 137)}
           {renderTutorialSection("Common Patterns 1", commonPatternsLessons, 191)}
           {renderTutorialSection("Basic Level 2", basicLevel2Lessons, 202)}
           {renderTutorialSection("Tricky Words 2", trickyWords2Lessons, 233)}
           {renderTutorialSection("Numbers", numbersLessons, 244)}
           {renderTutorialSection("Common Patterns 2", commonPatterns2Lessons, 274)}
           {renderTutorialSection("Basic Level 3", basicLevel3Lessons, 285)}
           {renderTutorialSection("Symbols", symbolsLessons, 316)}
           {renderTutorialSection("Common Patterns 3", commonPatterns3Lessons, 346)}
           {renderTutorialSection("Advanced Level 1", advancedLevel1Lessons, 357, "Speed Goal 45 WPM")}
           {renderTutorialSection("More Symbols", moreSymbolsLessons, 388)}
           {renderTutorialSection("Tricky Words 3", trickyWords3Lessons, 418)}
           {renderTutorialSection("Advanced Level 2", advancedLevel2Lessons, 429, "Speed Goal 50 WPM")}
           {renderTutorialSection("Advanced Level 3", advancedLevel3Lessons, 460, "Speed Goal 55 WPM")}
           {renderTutorialSection("Advanced Level 4", advancedLevel4Lessons, 491, "Speed Goal 60 WPM")}
           {renderTutorialSection("Advanced Level 5", advancedLevel5Lessons, 524, "Speed Goal 63 WPM")}
           {renderTutorialSection("Advanced Level 6", advancedLevel6Lessons, 557, "Speed Goal 66 WPM")}
           {renderTutorialSection("Advanced Level 7", advancedLevel7Lessons, 590, "Speed Goal 69 WPM")}
           {renderTutorialSection("Advanced Level 8", advancedLevel8Lessons, 621, "Speed Goal 72 WPM")}
           {renderTutorialSection("Advanced Level 9", advancedLevel9Lessons, 652, "Speed Goal 75 WPM")}
        </div>
      );
    }
    if (tab === 'games') {
        const handleBack = () => setSelectedGame(null);
        const catalogGame = CATALOG_GAMES.find(g => g.id === selectedGame);
        if (catalogGame) {
            return <GameContainer title={catalogGame.title} icon={catalogGame.icon} subtitle={catalogGame.subtitle} onBack={handleBack}>{catalogGame.render(user)}</GameContainer>;
        }
        switch (selectedGame) {
            case 'solo': return <GameContainer title="Timed Race" icon="⏱️" subtitle="Speed drill: type as many words as you can before the clock runs out." onBack={handleBack}><TypingGame user={user} /></GameContainer>;
            case 'multiplayer': return <GameContainer title="Multiplayer Race" icon="🏁" subtitle="Live competition: race your classmates and see who types fastest." onBack={handleBack}><MultiplayerRace user={user} classId={classId} /></GameContainer>;
            case 'word-burst': return <GameContainer title="Word Burst" icon="💥" subtitle="Vocabulary drill: type each word fully before it fades away." onBack={handleBack}><WordBurstGame user={user} /></GameContainer>;
            case 'bubble-pop': return <GameContainer title="Bubble Pop" icon="🫧" subtitle="Key recall: hit the letter shown on each bubble before it floats off." onBack={handleBack}><BubblePopGame user={user} /></GameContainer>;
            case 'car-race': return <GameContainer title="Car Race" icon="🏎️" subtitle="Speed drill: type words correctly to keep your car in the lead." onBack={handleBack}><CarRaceGame /></GameContainer>;
            case 'typing-rpg': return <GameContainer title="Typing RPG: The Cave of Curses" icon="🐉" subtitle="Adventure mode: type commands to battle monsters and save the kingdom." onBack={handleBack}><TypingRpgGame user={user} /></GameContainer>;
            case 'falling-words': return <GameContainer title="Falling Words" icon="🌧️" subtitle="Reaction drill: clear words before they reach the bottom." onBack={handleBack}><FallingWordsGame user={user} /></GameContainer>;
            case 'search-of-the-67': return <GameContainer title="In Search of the 67" icon="🗝️" subtitle="Accuracy challenge: type each of the 67 challenges perfectly to advance." onBack={handleBack}><SearchOfThe67Game user={user} /></GameContainer>;
            case 'turkey-chase': return <GameContainer title="Who Will Catch the Turkey?" icon="🦃" subtitle="Reflex drill: type fast and clean -- 3 mistakes and the turkey gets away." onBack={handleBack}><TurkeyChaseGame /></GameContainer>;

                default:
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {CATALOG_GAMES.filter(g => g.featured).map(g => (
                            <GameCard key={g.id} title={g.title} description={g.description} onClick={() => setSelectedGame(g.id)} color={g.color} icon={g.icon} skill={g.skill} />
                        ))}
                        <GameCard title="Who Will Catch the Turkey?" description="A Thanksgiving race! Type fast and don't make mistakes to catch the turkey." onClick={() => setSelectedGame('turkey-chase')} color="red" icon="🦃" skill="Reflexes" />
                        <GameCard title="Timed Race" description="Type as many words as you can in 60 seconds." onClick={() => setSelectedGame('solo')} color="blue" icon="⏱️" skill="Speed"/>
                        <GameCard title="Word Burst" description="Type the words before they disappear from the screen." onClick={() => setSelectedGame('word-burst')} color="purple" icon="💥" skill="Vocabulary"/>
                        <GameCard title="Bubble Pop" description="Pop the bubbles by typing the correct letter." onClick={() => setSelectedGame('bubble-pop')} color="red" icon="🫧" skill="Key Recall"/>
                        <GameCard title="Car Race" description="Type words correctly to speed to the finish line." onClick={() => setSelectedGame('car-race')} color="red" icon="🏎️" skill="Speed"/>
                        <GameCard title="Falling Words" description="Type the words before they hit the bottom!" onClick={() => setSelectedGame('falling-words')} color="blue" icon="🌧️" skill="Reaction Time"/>
                        <GameCard title="In Search of the 67" description="A typing adventure! One mistake and you start over." onClick={() => setSelectedGame('search-of-the-67')} color="purple" icon="🗝️" skill="Accuracy" />
                        <GameCard title="Typing RPG" description="An epic adventure! Save your village by mastering the keyboard, level by level." onClick={() => setSelectedGame('typing-rpg')} color="blue" icon="🐉" skill="Adventure"/>
                        <GameCard title="Multiplayer Race" description="Race against your classmates in real-time!" onClick={() => setSelectedGame('multiplayer')} color="green" icon="🏁" skill="Competition"/>
                        {CATALOG_GAMES.filter(g => !g.featured).map(g => (
                            <GameCard key={g.id} title={g.title} description={g.description} onClick={() => setSelectedGame(g.id)} color={g.color} icon={g.icon} skill={g.skill} />
                        ))}
                    </div>
                );
        }
    }
    if (tab === 'badges') return <BadgesTab student={user} />;
    if (tab === 'progress') return <StudentProgressTab student={user} />;
    return null;
  };

  return (
    <div className="w-full max-w-7xl">
      <div className="flex border-b border-gray-300 mb-6">
        <button className={`py-2 px-6 font-semibold ${tab === 'lessons' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => { setSelectedLesson(null); setSelectedGame(null); setTab('lessons'); }}>Lessons</button>
        <button className={`py-2 px-6 font-semibold ${tab === 'games' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setTab('games')}>Games</button>
        <button className={`py-2 px-6 font-semibold ${tab === 'badges' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setTab('badges')}>Badges</button>
        <button className={`py-2 px-6 font-semibold ${tab === 'progress' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setTab('progress')}>My Progress</button>
      </div>
      <div>{renderContent()}</div>
    </div>
  );
};

export default StudentDashboard;
